/* eslint-disable @next/next/no-img-element */
import { useEffect, useState, useCallback } from "react";
import { render } from "react-dom";
import { Map, useMap } from "react-mapkit";
import useSWR, { useSWRConfig } from "swr";
import equal from "deep-equal";
import fetcher from "utils/fetcher";
import { SEARCH_TOKEN, SEARCH_URL } from "utils/const";
import { MetersFromMiles } from "utils/units";
import BuildLink from "utils/build-link";

export default function MapEmbed() {
  const [annotations, setAnnotations] = useState([]);
  const { mapkit, map, mapProps } = useMap({
    showsPointsOfInterest: false,
    showsUserLocation: true,
    tracksUserLocation: true,
    showsUserLocationControl: true,
    _allowWheelToZoom: true,
  });
  const { mutate } = useSWRConfig();
  const { data: geo } = useSWR("/api/geo", fetcher, {
    fallback: { latitude: 33.812062499999996, longitude: -117.91893750000001 },
  });

  const { data: searchResults } = useSWR("map", () => {
    if (!map) {
      return;
    }

    const center = map?.region?.center;
    const span = map?.region?.span;
    const searchDelta = Math.max(
      span?.latitudeDelta || 0.16,
      span?.longitudeDelta || 0.16
    );
    const searchDistance = searchDelta * 111000;

    return fetcher({
      method: "POST",
      url: `${SEARCH_URL}/indexes/omnisearch/search`,
      data: {
        filter: `_geoRadius(${center?.latitude || geo.latitude},${
          center?.longitude || geo.longitude
        }, ${searchDistance}) AND (contentType=park ${
          searchDistance < 2000 ? "OR contentType=attraction" : ""
        })`,
        sort: [
          `_geoPoint(${center?.latitude || geo.latitude},${
            center?.longitude || geo.longitude
          }):asc`,
        ],
        limit: 500,
      },
      headers: {
        Authorization: `Bearer ${SEARCH_TOKEN}`,
      },
    });
  });

  useEffect(() => {
    if (geo?.latitude && geo?.longitude && map && mapkit) {
      const spanDelta = MetersFromMiles(100) / 111000;
      const center = new mapkit.Coordinate(geo.latitude, geo.longitude);
      const span = new mapkit.CoordinateSpan(spanDelta, spanDelta);
      const region = new mapkit.CoordinateRegion(center, span);
      map.setRegionAnimated(region, false);
      mutate("map");
    }
  }, [geo, map, mapkit, mutate]);

  useEffect(() => {
    if (!searchResults || !mapkit) {
      return;
    }

    const hits =
      searchResults?.hits?.filter((hit) => typeof hit.id === "string") || [];

    const calloutDelegate = {
      calloutElementForAnnotation: function (annotation) {
        const {
          photo: image,
          url,
          title,
          contentType,
          parentName,
        } = annotation.data;
        const isRestroom = title.toLowerCase().includes("restroom");

        const callout = (
          <div className="relative h-full w-full bg-white shadow-xl">
            <div className="flex h-full flex-col md:flex-row">
              <div className="flex h-1/2 w-full md:h-full md:w-1/2">
                {image ? (
                  <img
                    src={`https://images.opencoaster.com/800x400/${image.hash}${image.ext}`}
                    srcSet={`
                https://images.opencoaster.com/800x400/${image.hash}${image.ext} 800w,
                https://images.opencoaster.com/600x300/${image.hash}${image.ext} 600w,
                https://images.opencoaster.com/400x200/${image.hash}${image.ext} 400w,
                https://images.opencoaster.com/300x150/${image.hash}${image.ext} 300w,
                https://images.opencoaster.com/200x100/${image.hash}${image.ext} 200w,
                https://images.opencoaster.com/100x50/${image.hash}${image.ext} 100w
              `}
                    alt={image.alternativeText ? image.alternativeText : title}
                    className="h-full w-full object-cover object-center"
                  />
                ) : (
                  <img
                    className="h-full w-full bg-gray-200 object-contain object-center"
                    src={`/placeholder-${
                      isRestroom ? "restroom" : contentType
                    }.svg`}
                    alt={title}
                  />
                )}
              </div>
              <div className="flex h-1/2 w-full flex-col justify-center p-4 md:h-full md:w-1/2">
                {contentType === "attraction" && parentName && (
                  <div className="mt-3 text-sm text-gray-500">{parentName}</div>
                )}
                <a href={url}>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {title}
                  </h3>
                </a>
                <a
                  href={url}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  View {isRestroom ? "restroom" : contentType} →
                </a>
              </div>
            </div>
          </div>
        );

        const element = document.createElement("div");
        element.className = "w-[300px] md:w-[500px] h-[320px] md:h-[160px]";
        render(callout, element);

        return element;
      },
    };

    setAnnotations(
      hits.map((hit) => {
        const isRestroom = hit.title.toLowerCase().includes("restroom");
        return new mapkit.MarkerAnnotation(
          new mapkit.Coordinate(hit._geo.lat, hit._geo.lng),
          {
            title: hit.title,
            subtitle: hit.parentName,
            clusteringIdentifier: hit.contentType,
            glyphImage: {
              1: `/placeholder-${
                isRestroom ? "restroom" : hit.contentType
              }.svg`,
            },
            callout: calloutDelegate,
            data: {
              url: BuildLink(hit),
              ...hit,
            },
          }
        );
      })
    );
  }, [searchResults, mapkit, map]);

  useEffect(() => {
    if (map && Array.isArray(annotations)) {
      if (!equal(map.annotations, annotations)) {
        map.annotations = annotations;
      }
    }
  }, [annotations, map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const onRegionChange = () => {
      mutate("map");
    };

    map.addEventListener("region-change-end", onRegionChange);

    return () => {
      map.removeEventListener("region-change-end", onRegionChange);
    };
  }, [map, mutate]);

  return <Map {...mapProps} />;
}
